// inventory.actions.js
import axiosInstance from '../helpers/axios';
import { inventoryConstants } from './constants';

const errMsg = (error, fallback = 'Ошибка запроса') => {
    return error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback;
};

const ensureOfficeId = (inputOfficeId, getState) => {
    const fromState = getState()?.office?.currentOfficeId;
    const officeId = inputOfficeId || fromState;

    if (!officeId || officeId === 'all') {
        throw new Error('officeId обязателен для inventory API');
    }

    return officeId;
};

const normalizeSummaryArgs = (input, legacyOfficeId) => {
    if (input && typeof input === 'object') {
        return input;
    }

    return {
        warehouseId: input,
        officeId: legacyOfficeId,
    };
};

export const getWarehouses = (officeId) => async (dispatch, getState) => {
    try {
        dispatch({ type: inventoryConstants.GET_WAREHOUSES_REQUEST });

        const oid = ensureOfficeId(officeId, getState);
        const res = await axiosInstance.get('/inventory/warehouse', {
            params: { officeId: oid },
        });

        dispatch({
            type: inventoryConstants.GET_WAREHOUSES_SUCCESS,
            payload: { warehouses: res.data.warehouses || [] },
        });

        return res.data;
    } catch (error) {
        const message = errMsg(error);

        dispatch({
            type: inventoryConstants.GET_WAREHOUSES_FAILURE,
            payload: { error: message },
        });

        throw error;
    }
};

export const createWarehouse = ({ officeId, ...data }) => async (dispatch, getState) => {
    try {
        dispatch({ type: inventoryConstants.CREATE_WAREHOUSE_REQUEST });

        const oid = ensureOfficeId(officeId, getState);
        const res = await axiosInstance.post('/inventory/warehouse', {
            ...data,
            officeId: oid,
        });

        dispatch({
            type: inventoryConstants.CREATE_WAREHOUSE_SUCCESS,
            payload: { warehouse: res.data.warehouse },
        });

        return res.data;
    } catch (error) {
        const message = errMsg(error);

        dispatch({
            type: inventoryConstants.CREATE_WAREHOUSE_FAILURE,
            payload: { error: message },
        });

        throw error;
    }
};

export const updateWarehouse = (id, data, officeId) => async (dispatch, getState) => {
    try {
        dispatch({ type: inventoryConstants.UPDATE_WAREHOUSE_REQUEST });

        const oid = ensureOfficeId(officeId, getState);
        const res = await axiosInstance.patch(`/inventory/warehouse/${id}`, {
            ...data,
            officeId: oid,
        });

        dispatch({
            type: inventoryConstants.UPDATE_WAREHOUSE_SUCCESS,
            payload: { warehouse: res.data.warehouse },
        });

        return res.data;
    } catch (error) {
        const message = errMsg(error);

        dispatch({
            type: inventoryConstants.UPDATE_WAREHOUSE_FAILURE,
            payload: { error: message },
        });

        throw error;
    }
};

export const toggleWarehouseActive = (id, officeId) => async (dispatch, getState) => {
    try {
        dispatch({ type: inventoryConstants.TOGGLE_WAREHOUSE_REQUEST });

        const oid = ensureOfficeId(officeId, getState);
        const res = await axiosInstance.post(`/inventory/warehouse/${id}/toggle`, {
            officeId: oid,
        });

        dispatch({
            type: inventoryConstants.TOGGLE_WAREHOUSE_SUCCESS,
            payload: { warehouse: res.data.warehouse },
        });

        return res.data;
    } catch (error) {
        const message = errMsg(error);

        dispatch({
            type: inventoryConstants.TOGGLE_WAREHOUSE_FAILURE,
            payload: { error: message },
        });

        throw error;
    }
};

export const getStockSummary = (input, legacyOfficeId) => async (dispatch, getState) => {
    try {
        dispatch({ type: inventoryConstants.GET_STOCK_SUMMARY_REQUEST });

        const args = normalizeSummaryArgs(input, legacyOfficeId);
        const oid = ensureOfficeId(args.officeId, getState);

        const res = await axiosInstance.get('/inventory/stock/summary', {
            params: {
                warehouseId: args.warehouseId,
                officeId: oid,
            },
        });

        dispatch({
            type: inventoryConstants.GET_STOCK_SUMMARY_SUCCESS,
            payload: { stock: res.data.stock || [] },
        });

        return res.data;
    } catch (error) {
        const message = errMsg(error);

        dispatch({
            type: inventoryConstants.GET_STOCK_SUMMARY_FAILURE,
            payload: { error: message },
        });

        throw error;
    }
};

export const listBatches = (input = {}, legacyOfficeId) => async (dispatch, getState) => {
    try {
        dispatch({ type: inventoryConstants.LIST_BATCHES_REQUEST });

        const params = input && typeof input === 'object'
            ? input
            : { warehouseId: input, officeId: legacyOfficeId };

        const oid = ensureOfficeId(params.officeId || legacyOfficeId, getState);

        const res = await axiosInstance.get('/inventory/stock/batches', {
            params: {
                warehouseId: params.warehouseId,
                materialId: params.materialId,
                officeId: oid,
            },
        });

        dispatch({
            type: inventoryConstants.LIST_BATCHES_SUCCESS,
            payload: { batches: res.data.batches || [] },
        });

        return res.data;
    } catch (error) {
        const message = errMsg(error);

        dispatch({
            type: inventoryConstants.LIST_BATCHES_FAILURE,
            payload: { error: message },
        });

        throw error;
    }
};

export const receiveStock = ({
    warehouseId,
    materialId,
    qty,
    unitCost,
    supplier,
    lot,
    expiresAt,
    officeId,
}) => async (dispatch, getState) => {
    try {
        dispatch({ type: inventoryConstants.RECEIVE_STOCK_REQUEST });

        const oid = ensureOfficeId(officeId, getState);

        const res = await axiosInstance.post('/inventory/stock/receive', {
            warehouseId,
            materialId,
            qty: Number(qty),
            unitCost: Number(unitCost || 0),
            supplier,
            lot,
            expiresAt,
            officeId: oid,
        });

        dispatch({
            type: inventoryConstants.RECEIVE_STOCK_SUCCESS,
            payload: { batch: res.data.batch },
        });

        if (warehouseId) {
            dispatch(getStockSummary({ warehouseId, officeId: oid }));
            dispatch(listBatches({ warehouseId, officeId: oid }));
            dispatch(getStockMovements({ officeId: oid, warehouseId, limit: 200 }));
        }

        return res.data;
    } catch (error) {
        const message = errMsg(error);

        dispatch({
            type: inventoryConstants.RECEIVE_STOCK_FAILURE,
            payload: { error: message },
        });

        throw error;
    }
};

export const adjustStock = ({
    batchId,
    deltaQty,
    warehouseId,
    officeId,
    reason,
}) => async (dispatch, getState) => {
    try {
        dispatch({ type: inventoryConstants.ADJUST_STOCK_REQUEST });

        const oid = ensureOfficeId(officeId, getState);

        const res = await axiosInstance.post('/inventory/stock/adjust', {
            batchId,
            deltaQty: Number(deltaQty),
            reason,
            officeId: oid,
        });

        dispatch({
            type: inventoryConstants.ADJUST_STOCK_SUCCESS,
            payload: { batch: res.data.batch },
        });

        if (warehouseId) {
            dispatch(getStockSummary({ warehouseId, officeId: oid }));
            dispatch(listBatches({ warehouseId, officeId: oid }));
            dispatch(getStockMovements({ officeId: oid, warehouseId, limit: 200 }));
        }

        return res.data;
    } catch (error) {
        const message = errMsg(error);

        dispatch({
            type: inventoryConstants.ADJUST_STOCK_FAILURE,
            payload: { error: message },
        });

        throw error;
    }
};

export const transferStock = ({
    batchId,
    toWarehouseId,
    qty,
    warehouseId,
    officeId,
}) => async (dispatch, getState) => {
    try {
        dispatch({ type: inventoryConstants.TRANSFER_STOCK_REQUEST });

        const oid = ensureOfficeId(officeId, getState);

        const res = await axiosInstance.post('/inventory/stock/transfer', {
            batchId,
            toWarehouseId,
            qty: Number(qty),
            officeId: oid,
        });

        dispatch({
            type: inventoryConstants.TRANSFER_STOCK_SUCCESS,
            payload: {
                from: res.data.from,
                to: res.data.to,
            },
        });

        if (warehouseId) {
            dispatch(getStockSummary({ warehouseId, officeId: oid }));
            dispatch(listBatches({ warehouseId, officeId: oid }));
            dispatch(getStockMovements({ officeId: oid, warehouseId, limit: 200 }));
        }

        if (toWarehouseId && toWarehouseId !== warehouseId) {
            dispatch(getStockSummary({ warehouseId: toWarehouseId, officeId: oid }));
        }

        return res.data;
    } catch (error) {
        const message = errMsg(error);

        dispatch({
            type: inventoryConstants.TRANSFER_STOCK_FAILURE,
            payload: { error: message },
        });

        throw error;
    }
};

export const cleanupEmptyBatches = (officeId) => async (dispatch, getState) => {
    try {
        dispatch({ type: inventoryConstants.CLEANUP_STOCK_REQUEST });

        const oid = ensureOfficeId(officeId, getState);
        const res = await axiosInstance.delete('/inventory/stock/cleanup', {
            data: { officeId: oid },
        });

        dispatch({
            type: inventoryConstants.CLEANUP_STOCK_SUCCESS,
            payload: { deleted: res.data.deleted },
        });

        return res.data;
    } catch (error) {
        const message = errMsg(error);

        dispatch({
            type: inventoryConstants.CLEANUP_STOCK_FAILURE,
            payload: { error: message },
        });

        throw error;
    }
};

export const upsertBom = (productId, components, officeId) => async (dispatch, getState) => {
    try {
        dispatch({ type: inventoryConstants.UPSERT_BOM_REQUEST });

        const oid = ensureOfficeId(officeId, getState);
        const res = await axiosInstance.post('/inventory/bom', {
            productId,
            components,
            officeId: oid,
        });

        dispatch({
            type: inventoryConstants.UPSERT_BOM_SUCCESS,
            payload: { bom: res.data.bom },
        });

        return res.data;
    } catch (error) {
        const message = errMsg(error);

        dispatch({
            type: inventoryConstants.UPSERT_BOM_FAILURE,
            payload: { error: message },
        });

        throw error;
    }
};

export const getBom = (productId, officeId) => async (dispatch, getState) => {
    try {
        dispatch({
            type: inventoryConstants.GET_BOM_REQUEST,
            meta: { productId },
        });

        const oid = ensureOfficeId(officeId, getState);
        const res = await axiosInstance.get(`/inventory/bom/${productId}`, {
            params: { officeId: oid },
        });

        dispatch({
            type: inventoryConstants.GET_BOM_SUCCESS,
            payload: { productId, bom: res.data?.bom || null },
        });

        return res.data;
    } catch (error) {
        const message = errMsg(error);

        dispatch({
            type: inventoryConstants.GET_BOM_FAILURE,
            payload: { error: message, productId },
        });

        throw error;
    }
};

export const deleteBom = (productId, officeId) => async (dispatch, getState) => {
    try {
        dispatch({
            type: inventoryConstants.DELETE_BOM_REQUEST,
            meta: { productId },
        });

        const oid = ensureOfficeId(officeId, getState);

        await axiosInstance.delete(`/inventory/bom/${productId}`, {
            data: { officeId: oid },
        });

        dispatch({
            type: inventoryConstants.DELETE_BOM_SUCCESS,
            payload: { productId },
        });

        return true;
    } catch (error) {
        const message = errMsg(error);

        dispatch({
            type: inventoryConstants.DELETE_BOM_FAILURE,
            payload: { error: message, productId },
        });

        throw error;
    }
};

export const reserveForOrderItem = ({
    orderId,
    orderItem,
    warehouseId,
    officeId,
}) => async (dispatch, getState) => {
    try {
        dispatch({ type: inventoryConstants.RESERVE_REQUEST });

        const oid = ensureOfficeId(officeId, getState);
        const res = await axiosInstance.post('/inventory/reserve', {
            orderId,
            orderItem,
            warehouseId,
            officeId: oid,
        });

        dispatch({
            type: inventoryConstants.RESERVE_SUCCESS,
            payload: {
                reservation: res.data.reservation,
                allocations: res.data.allocations,
            },
        });

        if (warehouseId) {
            dispatch(getStockSummary({ warehouseId, officeId: oid }));
            dispatch(listBatches({ warehouseId, officeId: oid }));
            dispatch(getStockMovements({ officeId: oid, warehouseId, limit: 200 }));
        }

        return res.data;
    } catch (error) {
        const message = errMsg(error);

        dispatch({
            type: inventoryConstants.RESERVE_FAILURE,
            payload: { error: message },
        });

        throw error;
    }
};

export const consumeReservation = (reservationId, officeId, warehouseId) => async (dispatch, getState) => {
    try {
        dispatch({ type: inventoryConstants.CONSUME_RESERVATION_REQUEST });

        const oid = ensureOfficeId(officeId, getState);
        const res = await axiosInstance.post('/inventory/reserve/consume', {
            reservationId,
            officeId: oid,
        });

        dispatch({
            type: inventoryConstants.CONSUME_RESERVATION_SUCCESS,
            payload: { reservation: res.data.reservation },
        });

        if (warehouseId) {
            dispatch(getStockSummary({ warehouseId, officeId: oid }));
            dispatch(listBatches({ warehouseId, officeId: oid }));
            dispatch(getStockMovements({ officeId: oid, warehouseId, limit: 200 }));
        }

        return res.data;
    } catch (error) {
        const message = errMsg(error);

        dispatch({
            type: inventoryConstants.CONSUME_RESERVATION_FAILURE,
            payload: { error: message },
        });

        throw error;
    }
};

export const cancelReservation = (reservationId, officeId, warehouseId) => async (dispatch, getState) => {
    try {
        dispatch({ type: inventoryConstants.CANCEL_RESERVATION_REQUEST });

        const oid = ensureOfficeId(officeId, getState);
        const res = await axiosInstance.post('/inventory/reserve/cancel', {
            reservationId,
            officeId: oid,
        });

        dispatch({
            type: inventoryConstants.CANCEL_RESERVATION_SUCCESS,
            payload: { reservation: res.data.reservation },
        });

        if (warehouseId) {
            dispatch(getStockSummary({ warehouseId, officeId: oid }));
            dispatch(listBatches({ warehouseId, officeId: oid }));
            dispatch(getStockMovements({ officeId: oid, warehouseId, limit: 200 }));
        }

        return res.data;
    } catch (error) {
        const message = errMsg(error);

        dispatch({
            type: inventoryConstants.CANCEL_RESERVATION_FAILURE,
            payload: { error: message },
        });

        throw error;
    }
};

export const getReservationsByOrder = (orderId, officeId) => async (dispatch, getState) => {
    try {
        dispatch({
            type: inventoryConstants.GET_RESERVATIONS_BY_ORDER_REQUEST,
            meta: { orderId },
        });

        const oid = ensureOfficeId(officeId, getState);
        const res = await axiosInstance.get(`/inventory/reserve/order/${orderId}`, {
            params: { officeId: oid },
        });

        dispatch({
            type: inventoryConstants.GET_RESERVATIONS_BY_ORDER_SUCCESS,
            payload: {
                orderId,
                reservations: res.data.reservations || [],
            },
        });

        return res.data;
    } catch (error) {
        const message = errMsg(error);

        dispatch({
            type: inventoryConstants.GET_RESERVATIONS_BY_ORDER_FAILURE,
            payload: { error: message, orderId },
        });

        throw error;
    }
};

export const getMaterials = ({ officeId, scope = 'all', q = '' } = {}) => async (dispatch, getState) => {
    try {
        dispatch({ type: inventoryConstants.GET_MATERIALS_REQUEST });

        const oid = ensureOfficeId(officeId, getState);
        const res = await axiosInstance.get('/inventory/materials', {
            params: {
                officeId: oid,
                scope,
                q: q || undefined,
            },
        });

        dispatch({
            type: inventoryConstants.GET_MATERIALS_SUCCESS,
            payload: { materials: res.data.materials || [] },
        });

        return res.data;
    } catch (error) {
        const message = errMsg(error, 'Не удалось загрузить материалы');

        dispatch({
            type: inventoryConstants.GET_MATERIALS_FAILURE,
            payload: { error: message },
        });

        throw error;
    }
};

export const createMaterial = (payload) => async (dispatch, getState) => {
    try {
        dispatch({ type: inventoryConstants.CREATE_MATERIAL_REQUEST });

        const oid = ensureOfficeId(payload?.officeId, getState);
        const res = await axiosInstance.post('/inventory/materials', {
            ...payload,
            officeId: oid,
        });

        dispatch({
            type: inventoryConstants.CREATE_MATERIAL_SUCCESS,
            payload: { material: res.data.material },
        });

        return res.data;
    } catch (error) {
        const message = errMsg(error, 'Не удалось создать материал');

        dispatch({
            type: inventoryConstants.CREATE_MATERIAL_FAILURE,
            payload: { error: message },
        });

        throw error;
    }
};

export const updateMaterial = (id, payload) => async (dispatch, getState) => {
    try {
        dispatch({ type: inventoryConstants.UPDATE_MATERIAL_REQUEST });

        const oid = ensureOfficeId(payload?.officeId, getState);
        const res = await axiosInstance.patch(`/inventory/materials/${id}`, {
            ...payload,
            officeId: oid,
        });

        dispatch({
            type: inventoryConstants.UPDATE_MATERIAL_SUCCESS,
            payload: { material: res.data.material },
        });

        return res.data;
    } catch (error) {
        const message = errMsg(error, 'Не удалось обновить материал');

        dispatch({
            type: inventoryConstants.UPDATE_MATERIAL_FAILURE,
            payload: { error: message },
        });

        throw error;
    }
};

export const archiveMaterial = (id, officeId) => async (dispatch, getState) => {
    try {
        dispatch({ type: inventoryConstants.ARCHIVE_MATERIAL_REQUEST });

        const oid = ensureOfficeId(officeId, getState);
        const res = await axiosInstance.post(`/inventory/materials/${id}/archive`, {
            officeId: oid,
        });

        dispatch({
            type: inventoryConstants.ARCHIVE_MATERIAL_SUCCESS,
            payload: {
                material: res.data.material,
                materialId: id,
            },
        });

        return res.data;
    } catch (error) {
        const message = errMsg(error, 'Не удалось архивировать материал');

        dispatch({
            type: inventoryConstants.ARCHIVE_MATERIAL_FAILURE,
            payload: { error: message },
        });

        throw error;
    }
};

export const getStockMovements = ({
    officeId,
    warehouseId = 'all',
    materialId = 'all',
    type = 'all',
    dateFrom = '',
    dateTo = '',
    limit = 200,
} = {}) => async (dispatch, getState) => {
    try {
        dispatch({ type: inventoryConstants.GET_STOCK_MOVEMENTS_REQUEST });

        const oid = ensureOfficeId(officeId, getState);
        const res = await axiosInstance.get('/inventory/stock/movements', {
            params: {
                officeId: oid,
                warehouseId,
                materialId,
                type,
                dateFrom: dateFrom || undefined,
                dateTo: dateTo || undefined,
                limit,
            },
        });

        dispatch({
            type: inventoryConstants.GET_STOCK_MOVEMENTS_SUCCESS,
            payload: { movements: res.data.movements || [] },
        });

        return res.data;
    } catch (error) {
        const message = errMsg(error, 'Не удалось загрузить журнал операций');

        dispatch({
            type: inventoryConstants.GET_STOCK_MOVEMENTS_FAILURE,
            payload: { error: message },
        });

        throw error;
    }
};